<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { dirtyFieldMixin } from '@/mixins/dirtyFieldMixin'
import { actionMenuMixin } from '@/mixins/actionMenuMixin'

import SoilProfileWedgeTableHeader from '@/components/soil-profile-wedges/SoilProfileWedgeTableHeader.vue'
import SoilProfileWedgeTableBody from '@/components/soil-profile-wedges/SoilProfileWedgeTableBody.vue'
import SoilWedgesTableActionMenu from '@/components/soil-profile-wedges/SoilWedgesTableActionMenu.vue'
import ImportFromPreviousStageDialog from '@/components/schematisationDetail/ImportFromPreviousStageDialog.vue'
import ActionBar from '@/components/common/ActionBar.vue'

const store = useStore()

const { dirtyItems, addDirty, getDirtyClass } = dirtyFieldMixin()
const { selectedItem, x, y, showMenu, show } = actionMenuMixin()

const showRowMenu = ref(false)
const selectedIndex = ref(null)
const xRow = ref(0)
const yRow = ref(0)
const importDialog = ref(false)

const currentStabSchematisationStage = computed(
  () => store.state.stabSchematisationStage.currentItem
)
const importLoading = computed(() => store.state.soilProfile.importLoading)
const xColumns = computed(() => store.state.soilLine.tableHeaders)

const soilLineItems = computed(() => store.state.soilLine.items)
const isLocked = computed(() => store.getters['stabSchematisation/isLockedCurrent'])
const schematisationStagesToImport = computed(
  () => store.getters['stabSchematisationStage/previousSchematisationStagesHaveSoilProfile']
)

const update = () => store.dispatch('soilLine/update')
const addSoilLine = (payload) => store.dispatch('soilLine/addSoilLine', payload)
const emptyDirtyItems = () => store.dispatch('soilLine/emptyDirtyItems')
const deleteXCoordinate = (index) => store.dispatch('soilLine/deleteXCoordinate', index)
const addNewXCoordinateAfter = (index) => store.dispatch('soilLine/addNewXCoordinateAfter', index)
const addNewXCoordinateBefore = (index) => store.dispatch('soilLine/addNewXCoordinateBefore', index)
const createSoilLine = () => store.dispatch('soilLine/create')
const deleteSoilLine = (index) => store.dispatch('soilLine/deleteSoilLine', index)
const importSoilLinesFromStage = (payload) =>
  store.dispatch('soilLine/importSoilLinesFromStage', payload)
const setTableHeaders = () => store.dispatch('soilLine/setTableHeaders')

const generateSoilLayer = (soilCompositionId) =>
  store.dispatch('soilLayer/generate', soilCompositionId)

const fetchStatePoints = (soilCompositionId) =>
  store.dispatch('statePoints/fetchItems', soilCompositionId)

const insertSoilLineAbove = (selectedItem) => {
  addSoilLine({ selectedItem })
}
const insertSoilLineBelow = (selectedItem) => {
  addSoilLine({ selectedItem, insertAfter: true })
}
const insertSoilProfileToTheLeft = (index) => {
  addNewXCoordinateBefore(index)
  addDirty(xColumns.value, xColumns.value[index].toString())
}
const insertSoilProfileToTheRight = (index) => {
  addNewXCoordinateAfter(index)
  addDirty(xColumns.value, xColumns.value[index + 1].toString())
}
const deleteColumnAndGenerate = async (index) => {
  await deleteXCoordinate(index)
  await update()
  await emptyDirtyItems()
  dirtyItems.value = []
  await getSoilLayer()
}
const deleteRowAndGenerate = async (selectedItem) => {
  await deleteSoilLine(selectedItem)
  await update()
  await emptyDirtyItems()
  dirtyItems.value = []
  await getSoilLayer()
}
const saveChanges = async () => {
  try {
    await update()
    await createSoilLine()
    await emptyDirtyItems()
    await getSoilLayer()
    dirtyItems.value = []
  } catch (e) {
    console.log(e)
  }
}
const getSoilLayer = async () => {
  try {
    const soilCompositionId = currentStabSchematisationStage.value['soilcomposition']
    await generateSoilLayer(soilCompositionId)
    await fetchStatePoints(soilCompositionId)
  } catch (e) {
    console.log(e)
  }
}

const showRow = async (index, x, y) => {
  selectedIndex.value = index
  showRowMenu.value = false
  xRow.value = x
  yRow.value = y
  await nextTick()
  showRowMenu.value = true
  showMenu.value = false
}

const handleImport = async (sourceSchematisationStageId) => {
  try {
    await emptyDirtyItems()
    await importSoilLinesFromStage({
      sourceSchematisationStageId,
      targetSchematisationStageId: currentStabSchematisationStage.value.id,
      soilCompositionId: currentStabSchematisationStage.value.soilcomposition
    })
    await getSoilLayer(currentStabSchematisationStage.value['soilcomposition'])
  } catch (e) {
    console.log(e)
  } finally {
    toggleImportDialog(false)
  }
}
const toggleImportDialog = (value) => {
  importDialog.value = value
}

watch(soilLineItems, () => {
  setTableHeaders()
})

const updateSelectedIndex = (index) => {
  selectedIndex.value = index
}

const updateSelectedItem = (item) => {
  selectedItem.value = item
}

onMounted(() => {
  setTableHeaders()
})
</script>

<template>
  <div class="d-flex fill-height flex-column">
    <ActionBar
      class="pa-3"
      :disabled="isLocked"
      border-bottom
      show-save
      :show-add="!soilLineItems.length"
      @save="saveChanges"
      show-import
      @add="addSoilLine({ number: 1 })"
      @import="toggleImportDialog(true)"
      :disabled-import="schematisationStagesToImport.length === 0"
    >
    </ActionBar>
    <div class="d-flex flex-column overflow-y-auto">
      <v-data-table density="compact" hide-no-data hide-default-footer class="soil-profile-wedge">
        <template #bottom></template>
        <template v-slot:thead>
          <SoilProfileWedgeTableHeader
            :selected-item="selectedIndex"
            :add-dirty="addDirty"
            :get-dirty-class="getDirtyClass"
            @update:selected-item="updateSelectedIndex"
            @right-click="showRow"
          />
        </template>
        <template v-slot:tbody>
          <SoilProfileWedgeTableBody
            :get-dirty-class="getDirtyClass"
            :add-dirty="addDirty"
            @update:selected-item="updateSelectedItem"
            @right-click="show"
          />
        </template>
      </v-data-table>
    </div>
    <SoilWedgesTableActionMenu
      :show-menu="showRowMenu"
      @update:show-menu="showRowMenu = $event"
      :x="xRow"
      :y="yRow"
      :delete-button="xColumns.length > 2"
      @addBefore="insertSoilProfileToTheLeft(selectedIndex)"
      @addAfter="insertSoilProfileToTheRight(selectedIndex)"
      @action="deleteColumnAndGenerate(selectedIndex)"
    />
    <SoilWedgesTableActionMenu
      :show-menu="showMenu"
      @update:show-menu="showMenu = $event"
      :x="x"
      :y="y"
      icon-before="mdi-chevron-up"
      icon-after="mdi-chevron-down"
      title="Soil layer"
      @addBefore="insertSoilLineAbove(selectedItem)"
      @addAfter="insertSoilLineBelow(selectedItem)"
      @action="deleteRowAndGenerate(selectedItem)"
    />
    <ImportFromPreviousStageDialog
      :schematisation-stages="schematisationStagesToImport"
      v-model="importDialog"
      :loading="importLoading"
      text="soil profile wedges"
      @close="toggleImportDialog(false)"
      @import="handleImport"
    />
  </div>
</template>

<style scoped>
:deep(.v-field__input) {
  padding: 0 !important;
}
</style>
